import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { usePageMeta } from "@/lib/seo";

/**
 * Public About / Contact / Privacy / Terms pages. They must stay reachable
 * without signing in — AdSense's review crawler looks for them at public URLs
 * linked from the site (see SiteFooter), not behind the Profile screen.
 */

export const CONTACT_EMAIL = "hola@northofsixclub.com";
const EFFECTIVE_DATE = "26 September 2026";

const mono = { fontFamily: "IBM Plex Mono, monospace" };
const serif = { fontFamily: "Cormorant Garamond, serif" };

const H2 = ({ children }) => (
  <h2 className="text-2xl md:text-3xl tracking-tighter text-[#2D302A] mt-10 mb-3" style={serif}>
    {children}
  </h2>
);
const P = ({ children }) => <p className="mb-4">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-5 mb-4 space-y-2">{children}</ul>;
const Mail = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="underline underline-offset-2 text-[#2D302A]">
    {CONTACT_EMAIL}
  </a>
);
const ExtLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-[#2D302A]">
    {children}
  </a>
);

const LegalShell = ({ title, description, updated, children, testId }) => {
  const navigate = useNavigate();

  usePageMeta(title, description);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);

  // Came in from a direct link (e.g. the crawler, or a shared URL) → no history to go back to.
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/"));

  return (
    <div className="min-h-screen bg-[#FAF9F5]" data-testid={testId}>
      <header className="border-b border-[#E4E1D9] px-6 md:px-12 py-4 flex items-center gap-4">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={mono}
        >
          <ArrowLeft size={16} /> Back
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 md:py-14 text-[15px] leading-relaxed text-[#3D3F3A]">
        <h1 className="text-4xl md:text-5xl tracking-tighter text-[#2D302A]" style={serif}>
          {title}
        </h1>
        {updated && (
          <p className="mt-2 mb-8 text-[11px] uppercase tracking-[0.2em] text-[#9A9A98]" style={mono}>
            Last updated {updated}
          </p>
        )}
        <div className={updated ? "" : "mt-8"}>{children}</div>
      </main>
    </div>
  );
};

export const About = () => (
  <LegalShell title="About North of Six Club" description="North of Six Club is a community where tall people share the clothes and shoes that actually fit their height and size." testId="about-page">
    <P>
      North of Six Club is a community for tall people — six feet and up — who are tired of
      guessing whether a pair of jeans, a shirt or a pair of shoes will actually fit. Members
      share the clothes and shoes that genuinely work for their height and size, so the next
      tall person can skip the returns and buy with confidence.
    </P>
    <H2>What you'll find here</H2>
    <UL>
      <li><strong>Fits by height.</strong> Products shared by members, filtered by height, waist and shoe size.</li>
      <li><strong>Stories.</strong> Members' own experiences of being tall and finding clothes that fit.</li>
      <li><strong>Offline spots.</strong> Physical stores in Indian cities where tall people have found their size.</li>
      <li><strong>Guides.</strong> Practical resources like our inseam guide to help you pick the right size.</li>
    </UL>
    <H2>How we keep the lights on</H2>
    <P>
      North of Six Club is free to use. Some product links are affiliate links, which means we
      may earn a small commission if you buy through them, at no extra cost to you. We may also
      show advertising. Neither affects which products appear — everything in the feed is shared
      by members because it fit them.
    </P>
    <H2>Get in touch</H2>
    <P>
      Questions, feedback or partnership ideas? Write to us at <Mail /> or visit
      our <Link to="/contact" className="underline underline-offset-2 text-[#2D302A]">contact page</Link>.
    </P>
  </LegalShell>
);

export const Contact = () => (
  <LegalShell title="Contact us" description="Get in touch with North of Six Club for feedback, content reports, data requests or partnerships." testId="contact-page">
    <P>We'd love to hear from you. The quickest way to reach us is by email:</P>
    <p className="mb-8 text-lg"><Mail /></p>
    <P>Write to us about:</P>
    <UL>
      <li>Questions or feedback about the site</li>
      <li>Reporting a product, story, spot or comment that breaks our <Link to="/terms" className="underline underline-offset-2 text-[#2D302A]">Terms</Link></li>
      <li>Accessing, correcting or deleting your personal data (see our <Link to="/privacy" className="underline underline-offset-2 text-[#2D302A]">Privacy Policy</Link>)</li>
      <li>Copyright complaints</li>
      <li>Brand partnerships and advertising</li>
    </UL>
    <P>We aim to reply within 2–3 working days.</P>
  </LegalShell>
);

export const Privacy = () => (
  <LegalShell title="Privacy Policy" description="How North of Six Club collects, uses and protects your information, including cookies, analytics and advertising." updated={EFFECTIVE_DATE} testId="privacy-page">
    <P>
      This Privacy Policy explains how North of Six Club ("we", "us", "our") collects, uses and
      shares information when you use northofsixclub.com and its installable web app (the
      "Service"). By using the Service you agree to this policy.
    </P>

    <H2>1. Information we collect</H2>
    <P><strong>Information you give us</strong></P>
    <UL>
      <li><strong>Account details</strong> from Google Sign-In: your name, email address and profile picture.</li>
      <li><strong>Profile details</strong> you choose to add: username, height, weight, waist size, shoe size, gender, city and, if provided, phone number.</li>
      <li><strong>Content you post</strong>: product links and fit notes, stories, offline spots, comments, likes and bookmarks, including any photos you upload.</li>
      <li><strong>Messages</strong> you send us by email.</li>
    </UL>
    <P><strong>Information collected automatically</strong></P>
    <UL>
      <li><strong>Usage data</strong> such as pages viewed, products opened, links clicked and approximate location derived from your IP address.</li>
      <li><strong>Device data</strong> such as browser type, operating system, screen size and language.</li>
      <li><strong>Cookies and similar technologies</strong> — see section 4.</li>
      <li><strong>Push notification subscription</strong>, if you allow notifications in your browser.</li>
    </UL>

    <H2>2. How we use your information</H2>
    <UL>
      <li>To create and run your account and keep you signed in.</li>
      <li>To show products that suit your height and sizes, and spots near your city.</li>
      <li>To display your content to the community under your username. Your real name, email, weight and phone number are never shown publicly.</li>
      <li>To send you notifications and emails about new products that match your size and other community updates. You can turn these off at any time.</li>
      <li>To understand how the Service is used and improve it.</li>
      <li>To show advertising, including personalised advertising where permitted.</li>
      <li>To keep the Service safe, prevent abuse and comply with the law.</li>
    </UL>

    <H2>3. How we share information</H2>
    <P>We do not sell your personal information. We share it only with:</P>
    <UL>
      <li><strong>Service providers</strong> who host and run the Service for us — cloud hosting and database providers, Google (sign-in and analytics) and our email delivery providers.</li>
      <li><strong>Advertising partners</strong>, including Google, as described in section 4.</li>
      <li><strong>Retailers</strong>, when you click a product link. Once you leave our site, the retailer's own privacy policy applies. Affiliate links let the retailer or affiliate network know the visit came from us.</li>
      <li><strong>Authorities</strong>, where required by law or to protect the rights and safety of our users or others.</li>
    </UL>

    <H2>4. Cookies, analytics and advertising</H2>
    <P>
      We use cookies and similar technologies to keep you signed in, remember your preferences,
      measure traffic and show ads.
    </P>
    <UL>
      <li><strong>Essential cookies</strong> keep you signed in and the Service working.</li>
      <li><strong>Analytics.</strong> We use Google Analytics and Google Tag Manager to understand how visitors use the Service. Learn more at <ExtLink href="https://policies.google.com/technologies/partner-sites">How Google uses information from sites that use its services</ExtLink>.</li>
      <li>
        <strong>Advertising.</strong> Third-party vendors, including Google, use cookies to serve
        ads based on your previous visits to this website or other websites. Google's use of
        advertising cookies enables it and its partners to serve ads to you based on your visits
        to this site and/or other sites on the Internet.
      </li>
    </UL>
    <P>
      You can opt out of personalised advertising by visiting Google's{" "}
      <ExtLink href="https://adssettings.google.com">Ads Settings</ExtLink>, or opt out of some
      third-party vendors' use of cookies for personalised advertising at{" "}
      <ExtLink href="https://www.aboutads.info/choices">www.aboutads.info</ExtLink>. You can
      also block or delete cookies in your browser settings, though parts of the Service may
      stop working.
    </P>

    <H2>5. How long we keep your data</H2>
    <P>
      We keep your account information for as long as your account is active. Content you post
      stays up until you delete it or your account is deleted. We may keep limited records longer
      where the law requires it or to resolve disputes.
    </P>

    <H2>6. Your rights and choices</H2>
    <UL>
      <li>Edit your profile details at any time from your Profile page.</li>
      <li>Delete your stories, spots and comments from where they appear.</li>
      <li>Turn off push notifications in your browser settings.</li>
      <li>Ask us to access, correct or delete your personal data, or delete your account, by writing to <Mail />.</li>
    </UL>
    <P>
      Depending on where you live (for example under India's Digital Personal Data Protection
      Act, 2023, or the GDPR), you may have further rights, including withdrawing consent and
      raising a complaint with a data protection authority.
    </P>

    <H2>7. Security</H2>
    <P>
      We use reasonable technical and organisational measures to protect your information,
      including encrypted connections (HTTPS). No system is completely secure, so we cannot
      guarantee absolute security.
    </P>

    <H2>8. Children</H2>
    <P>
      The Service is intended for people aged 18 and over. We do not knowingly collect personal
      data from children. If you believe a child has given us their data, contact us and we will
      delete it.
    </P>

    <H2>9. Changes to this policy</H2>
    <P>
      We may update this policy from time to time. We will change the "Last updated" date above
      and, for significant changes, let you know through the Service.
    </P>

    <H2>10. Contact</H2>
    <P>Questions about this policy or your data? Email <Mail />.</P>
  </LegalShell>
);

export const Terms = () => (
  <LegalShell title="Terms & Conditions" description="The terms for using North of Six Club, including community rules, your content, affiliate links and advertising." updated={EFFECTIVE_DATE} testId="terms-page">
    <P>
      These Terms govern your use of northofsixclub.com and its installable web app (the
      "Service"), operated by North of Six Club ("we", "us"). By using the Service you agree to
      these Terms. If you do not agree, please do not use the Service.
    </P>

    <H2>1. Who can use the Service</H2>
    <P>
      You must be at least 18 years old to create an account. You are responsible for keeping
      your Google account secure and for everything that happens under your account.
    </P>

    <H2>2. What the Service is</H2>
    <P>
      North of Six Club is a community where members share clothing and shoes that fit tall
      people. We do not sell products. Prices, availability and sizing on retailer websites can
      change at any time, and any purchase you make is between you and the retailer.
    </P>

    <H2>3. Affiliate links and advertising</H2>
    <P>
      Some links on the Service are affiliate links: if you buy through them we may earn a
      commission, at no extra cost to you. We also display advertising, including ads served by
      Google. Ads and affiliate relationships do not decide which products members share.
    </P>

    <H2>4. Your content</H2>
    <P>
      You own the content you post — product links, fit notes, stories, spots, comments and
      photos. By posting it you give us a worldwide, non-exclusive, royalty-free licence to host,
      display, reproduce and distribute it on and in connection with the Service (including in
      notifications, emails and social media promotion of the Service). This licence ends when
      you delete the content, except where it has already been shared or we must keep it by law.
    </P>
    <P>You confirm you have the right to post your content and that it follows these Terms.</P>

    <H2>5. Community rules</H2>
    <P>Please don't post content that:</P>
    <UL>
      <li>is false, misleading, spam or purely promotional;</li>
      <li>is hateful, harassing, sexually explicit, violent or otherwise offensive;</li>
      <li>infringes anyone's copyright, trademark, privacy or other rights;</li>
      <li>shares someone else's personal information without their permission;</li>
      <li>is illegal, or links to malware or scams.</li>
    </UL>
    <P>
      Don't try to break, overload, scrape or reverse-engineer the Service, or access accounts
      or data that aren't yours. We review shared products and may remove any content or suspend
      any account that breaks these rules, at our discretion.
    </P>

    <H2>6. Our content</H2>
    <P>
      The Service's design, logo, text, guides and software belong to us or our licensors. You
      may not copy or reuse them without our permission, other than sharing links to the Service.
    </P>

    <H2>7. Copyright complaints</H2>
    <P>
      If you believe content on the Service infringes your copyright, email <Mail /> with a
      description of the work, a link to the content and your contact details. We will review it
      and remove infringing content.
    </P>

    <H2>8. Disclaimers</H2>
    <P>
      The Service is provided "as is". Fit recommendations come from other members and are
      shared in good faith; we don't guarantee that any product will fit you or meet your
      expectations. We are not responsible for retailers' products, prices, delivery, returns or
      websites.
    </P>

    <H2>9. Limitation of liability</H2>
    <P>
      To the fullest extent permitted by law, we are not liable for any indirect, incidental or
      consequential loss arising from your use of the Service, and our total liability to you
      for any claim will not exceed ₹1,000.
    </P>

    <H2>10. Ending your account</H2>
    <P>
      You can stop using the Service at any time and ask us to delete your account by emailing
      <> </><Mail />. We may suspend or end your access if you break these Terms.
    </P>

    <H2>11. Changes</H2>
    <P>
      We may update these Terms from time to time. We will change the "Last updated" date above,
      and continuing to use the Service after a change means you accept the updated Terms.
    </P>

    <H2>12. Governing law</H2>
    <P>
      These Terms are governed by the laws of India, and the courts of India have jurisdiction
      over any dispute.
    </P>

    <H2>13. Contact</H2>
    <P>Questions about these Terms? Email <Mail />.</P>
  </LegalShell>
);
